import { APPLICATION_REASON, type ApplicationReason } from '@/config/constants'
import type { EligibleProduct } from '../types/session.types'

/**
 * The reasons the applicant may actually choose, derived from the eligibility
 * response held in the session.
 *
 * Lives here rather than in the eligibility slice because two features need it —
 * eligibility produces the products, application-details consumes them — and
 * application-session is the one slice both are allowed to import.
 *
 * Offering a reason the applicant is not eligible for guarantees a server
 * rejection two screens later, with no explanation of what they chose wrongly.
 */
export function selectableReasons(products: EligibleProduct[]): ApplicationReason[] {
  const reasons = new Set<ApplicationReason>()

  for (const product of products) {
    if (!product.eligible) continue
    for (const reason of product.reasons ?? []) reasons.add(reason)
  }

  return [...reasons]
}

/**
 * What the reason picker actually offers.
 *
 * `selectableReasons` answers "what did §4.1 permit". This answers "what do we put
 * in front of the citizen", and the two differ in exactly one case: an EMPTY list.
 *
 * That happens when the eligible product carries no `reasons` — the key is nullable
 * on the wire — or when a session is restored without `availableProducts`. With no
 * fallback the select holds nothing but its placeholder, so Continue can never
 * enable: a dead end, with no message, on the one control the citizen cannot
 * proceed without.
 *
 * Offering all six instead is recoverable. By this screen the eligibility check has
 * already established that an ID re-issue product exists — an empty list therefore
 * means lost state, not ineligibility — and §4.5 rejects a reason the applicant is
 * not eligible for with a 400 the screen already renders in its banner.
 *
 * applicationDetails.schema.ts applied this same fallback on the validation side.
 * Keeping it here means the options and the rule that validates them cannot drift.
 */
export function offerableReasons(products: EligibleProduct[]): ApplicationReason[] {
  const reasons = selectableReasons(products)
  return reasons.length > 0 ? reasons : Object.values(APPLICATION_REASON)
}
